import { COMMON } from './common'
import { FLOOR_POSITION_LABELS, FLOOR_TYPE_LABELS } from './enum-labels'
import { TERMS } from './terms'

const UNASSIGNED_FLOOR = 'Ohne Geschoss'

/** Failures the storey and room endpoints report back. */
export const ROOM_ERRORS = {
  codeTaken: (code: string) =>
    `Die Raum-ID „${code}“ ist in diesem Objekt bereits vergeben.`,
  codeAllocationFailed:
    'Es konnte keine freie Raum-ID vergeben werden. Bitte erneut versuchen.',
  floorNotInProperty: `Das Geschoss gehört nicht zu diesem ${TERMS.property}.`,
} as const

export const ROOMS = {
  /** What a room's mark falls back to while nobody has picked one. */
  iconFallback: 'Ohne eigenes Symbol trägt der Raum das Symbol der Raumart.',
  add: 'Raum hinzufügen',
  searchPlaceholder: 'Raum suchen',
  /** The tab holding a room's own record — its name, storey and dimensions. */
  dataTab: 'Raumdaten',
  /** The group of figures a room is measured in. Not „Maße“: the group label
   *  is uppercased, and CSS writes that ß as SS. */
  dimensions: 'Abmessungen',
  roomCodeLabel: TERMS.roomCode,
  delete: {
    confirmTitle: 'Raum löschen',
    confirmMessage:
      'Diesen Raum wirklich löschen? Alle zugeordneten Positionen werden mitgelöscht.',
  },
  floor: {
    unassigned: UNASSIGNED_FLOOR,
    // Labels derived from floor type + position. A floor's free-text name
    // replaces these when the user set one. The numbered variants apply only
    // once a property has more than one floor of that type.
    derived: {
      ...FLOOR_TYPE_LABELS,
      ...FLOOR_POSITION_LABELS,
      BASEMENT_NUMBERED: (n: number) => `${n}. UG`,
      UPPER_NUMBERED: (n: number) => `${n}. OG`,
      ATTIC_NUMBERED: (n: number) => `${n}. DG`,
    },
    // The picker names the storeys a user may create and spells out which
    // levels FULL_FLOOR covers; the derived labels above name what a storey
    // is once it exists.
    type: {
      ...FLOOR_TYPE_LABELS,
      FULL_FLOOR: `${FLOOR_TYPE_LABELS.FULL_FLOOR} (EG/OG)`,
    },
    add: 'Geschoss hinzufügen',
    empty:
      'Noch keine Geschosse angelegt. Leg zuerst ein Geschoss an, um Räume zu erfassen.',
    roomCount: (n: number) => (n === 1 ? `${n} Raum` : `${n} Räume`),
    modal: {
      editTitle: 'Geschoss bearbeiten',
      typeLabel: 'Geschosstyp',
      nameLabel: COMMON.designation,
      nameHelper: 'Leer lassen, um die automatische Bezeichnung zu verwenden.',
    },
    delete: {
      confirmTitle: 'Geschoss löschen',
      confirmMessage: 'Dieses Geschoss wirklich löschen?',
      confirmMessageWithRooms: (n: number) =>
        n === 1
          ? `Dieses Geschoss wirklich löschen? ${n} Raum wird „${UNASSIGNED_FLOOR}“ zugeordnet und kann danach neu eingehängt werden.`
          : `Dieses Geschoss wirklich löschen? ${n} Räume werden „${UNASSIGNED_FLOOR}“ zugeordnet und können danach neu eingehängt werden.`,
    },
  },
  roomCode: {
    hint: 'Die Raum-ID wird automatisch aus dem Geschoss gebildet. Ändere sie nur, wenn es einen konkreten Grund dafür gibt — sie wird in Auswertungen und Dokumenten referenziert.',
    confirm: {
      title: `${TERMS.roomCode} anpassen`,
      message: (floor: string) =>
        `Der Raum wechselt nach „${floor}“. Die Raum-ID sollte zum neuen Geschoss passen.`,
      submit: COMMON.apply,
      required: 'Die Raum-ID darf nicht leer sein.',
    },
  },
} as const
